import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Building2, Wallet, Settings, RotateCcw } from 'lucide-react';
import { useAccountsEdit } from './hooks/useAccountsEdit';

export function AccountsEditPage() {
  const navigate = useNavigate();
  const { formData, setFormData, isLoading, isSaving, updateAccount } = useAccountsEdit();

  if (isLoading) {
    return (
      <div className="w-full flex-1 min-w-0 animate-pulse space-y-4">
        <div className="h-16 rounded-md bg-white border border-slate-100 shadow-sm" />
        <div className="grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 h-96 rounded-md bg-white border border-slate-100" />
          <div className="h-64 rounded-md bg-white border border-slate-100" />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('?tab=account')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Chỉnh sửa tài khoản</h1>
            <p className="text-sm text-slate-500">Cập nhật thông tin tài khoản thanh toán.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('?tab=account')}
            className="hidden h-10 items-center gap-2 rounded-md border border-slate-200 px-4 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all active:scale-95 sm:flex"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Hủy</span>
          </button>
          <button
            onClick={updateAccount}
            disabled={isSaving || !formData.accountName}
            className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-bold text-white shadow-md shadow-emerald-700/10 hover:bg-emerald-800 disabled:opacity-50 transition-all active:scale-95"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}</span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Main Form */}
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Building2 className="h-4 w-4 text-emerald-600" />
              <span>Thông tin tài khoản</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tên gợi nhớ</label>
                <input
                  type="text"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ngân hàng</label>
                <input
                  type="text"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Số tài khoản</label>
                <input
                  type="text"
                  disabled
                  className="h-11 w-full rounded-md border border-slate-100 bg-slate-50 px-3 text-sm font-mono text-slate-400 cursor-not-allowed"
                  value={formData.accountNumber}
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Chủ tài khoản</label>
                <input
                  type="text"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all uppercase"
                  value={formData.accountHolder}
                  onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value.toUpperCase() })}
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Wallet className="h-4 w-4 text-emerald-600" />
              <span>Số dư & Tiền tệ</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 opacity-60">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Số dư ban đầu</label>
                <input
                  type="number"
                  disabled
                  className="h-11 w-full rounded-md border border-slate-100 bg-slate-50 px-3 text-sm cursor-not-allowed"
                  value={formData.initialBalance}
                />
              </div>

              <div className="space-y-1.5 opacity-60">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loại tiền</label>
                <input
                  type="text"
                  disabled
                  className="h-11 w-full rounded-md border border-slate-100 bg-slate-50 px-3 text-sm font-bold cursor-not-allowed"
                  value={formData.currency}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Settings className="h-4 w-4 text-emerald-600" />
              <span>Cấu hình</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Trạng thái</label>
                <select
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold outline-none focus:border-emerald-500 transition-all"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="active">Đang hoạt động</option>
                  <option value="inactive">Tạm ngưng</option>
                  <option value="archived">Lưu trữ</option>
                </select>
              </div>

              <div className="flex items-center gap-3 rounded-md bg-slate-50 p-3">
                <input
                  type="checkbox"
                  id="isDefault"
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <label htmlFor="isDefault" className="text-xs font-bold text-slate-700 cursor-pointer">
                  Đặt làm tài khoản mặc định
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-2">
             <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ghi chú</label>
             <textarea
               rows={3}
               className="w-full rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
               value={formData.description}
               onChange={(e) => setFormData({ ...formData, description: e.target.value })}
             />
          </div>
        </div>
      </div>
    </div>
  );
}
